'use client';

import clsx from 'clsx';
import Text from '@/components/Text/Text';
import styles from './Plans.module.scss';
import Button, { ButtonType } from '@/components/Button/Button';
import { mediaPath } from '@/mediaPath';
import Icon from '@/components/Icon';

type PlansSectionData = {
  id: number;
  title: string;
  description?: string;
  cards: {
    id: string;
    button: {
      id: number;
      link: string;
      title: string;
      type: ButtonType;
      iconName?: string;
      target: '_self' | '_blank' | string;
    };
    title: string;
    price: string;
    description: string;
    duration?: string;
    badge?: string | null;
    featuresTitle?: string;
    features: {
      id: number;
      title: string | null;
      list: {
        id: number;
        text: string | null;
      }[];
    } | null;
    designedForTitle?: string;
    designedForText?: string;
    upgradeTitle?: string;
    upgradeText?: string;
    upgradeItems: {
      id: number;
      title: string | null;
      list: {
        id: number;
        text: string | null;
      }[];
    } | null;
  }[];
  background: {
    url: string;
    alt: string;
  } | null;
  termsText?: string;
};

const checkIconStyle = {
  fontSize: 24,
  color: 'var(--text-accent-100)',
  alignSelf: 'flex-start',
  fontWeight: 700,
};

const PlansSection = ({ data }: { data: PlansSectionData }) => {
  const { cards } = data;
  const hasAnyBadge = cards?.some((card) => card.badge) ?? false;

  return (
    <section className={styles.plansSection}>
      <div className={styles.topContainer}>
        <Text tag='h2'>{data.title}</Text>
        <p className={styles.plansDescription}>{data.description}</p>
      </div>

      <div
        className={clsx(styles.plansWrapper, {
          [styles.withoutBackground]: !data.background?.url,
        })}
        style={data.background?.url ? {
          background: `linear-gradient(0deg, rgba(0, 44, 79, 0.50) 0%, rgba(0, 44, 79, 0.50) 100%), url(${
            mediaPath(data.background.url)
          }) lightgray 50% / cover no-repeat`,
        } : undefined}
      >
        <div className={styles.plansContainer}>
          {cards && cards.map((card) => {
            const shouldShowFeatures = card.featuresTitle || card.features?.title || card.features?.list?.length;
            const shouldShowDesignedFor = card.designedForTitle || card.designedForText;
            const shouldShowUpgrades = card.upgradeTitle || card.upgradeText || card.upgradeItems?.title || card.upgradeItems?.list?.length;
            return (
              <div
                key={card.id}
                className={clsx(styles.columnCompare, {
                  [styles.badgeExist]: hasAnyBadge,
                  [styles.withBadge]: !!card.badge,
                })}
              >
                {hasAnyBadge && <p className={styles.columnCompareBadge}>{card.badge}</p>}
                <div className={styles.columnCompareCard}>
                  <div className={styles.columnCompareTop}>
                    <Text
                      tag='h5'
                      className={styles.columnCompareTitle}
                    >
                      {card.title}
                    </Text>
                    <div className={styles.columnComparePrice}>
                      <p className={styles.columnComparePriceValue}>{card.price}</p>
                      <p className={styles.columnComparePriceDuration}>{card.duration}</p>
                    </div>
                  </div>
                  <div className={styles.columnCompareBottom}>
                    <div className={styles.columnCompareContent}>
                      <Text
                        tag='p'
                        className={styles.columnCompareDescription}
                      >
                        {card.description}
                      </Text>
                      {shouldShowFeatures && (
                      <div className={styles.columnCompareContentBlock}>
                        {card.featuresTitle && (
                        <Text
                          tag='p'
                          className={clsx(styles.contentBlockTitle, styles.featuresTitle)}
                        >
                          {card.featuresTitle}
                        </Text>
                        )}
                        {card.features?.title && (
                        <Text
                          tag='p'
                          className={styles.contentBlockSubtitle}
                        >
                          {card.features.title}
                        </Text>
                        )}
                        {card.features?.list && (
                        <ul className={styles.contentList}>
                          {card.features.list.map((feature) => (
                            <li
                              key={feature.id}
                              className={styles.contentListItem}
                            >
                              <Icon
                                name='check'
                                style={checkIconStyle}
                                aria-hidden
                              />
                              <span>{feature.text}</span>
                            </li>
                          ))}
                        </ul>
                        )}
                      </div>
                      )}

                      {shouldShowDesignedFor && (
                      <div className={styles.columnCompareContentBlock}>
                        {card.designedForTitle && (
                        <Text
                          tag='p'
                          className={styles.contentBlockTitle}
                        >
                          {card.designedForTitle}
                        </Text>
                        )}
                        {card.designedForText && (
                        <Text
                          tag='p'
                          className={styles.columnCompareDescription}
                        >
                          {card.designedForText}
                        </Text>
                        )}
                      </div>
                      )}

                      {shouldShowUpgrades && (
                      <div className={styles.columnCompareContentBlock}>
                        {card.upgradeTitle && (
                          <Text
                            tag='p'
                            className={styles.contentBlockTitle}
                          >
                            {card.upgradeTitle}
                          </Text>
                        )}
                        {card.upgradeText && (
                          <Text
                            tag='p'
                            className={styles.columnCompareDescription}
                          >
                            {card.upgradeText}
                          </Text>
                        )}
                        {card.upgradeItems?.list && (
                          <>
                            {card.upgradeItems?.title && (
                              <Text
                                tag='p'
                                className={styles.contentBlockSubtitle}
                              >
                                {card.upgradeItems.title}
                              </Text>
                            )}
                            <ul className={styles.contentList}>
                              {card.upgradeItems.list.map((feature) => (
                                <li
                                  key={feature.id}
                                  className={styles.contentListItem}
                                >
                                  <Icon
                                    name='check'
                                    className={styles.checkIcon}
                                    style={checkIconStyle}
                                    aria-hidden
                                  />
                                  <span>{feature.text}</span>
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                      </div>
                      )}
                    </div>

                    <Button
                      type={card.button.type}
                      className={styles.primaryButton}
                      iconName={card.button.iconName || 'arrow_forward'}
                      href={card.button.link}
                    >
                      {card.button.title}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {data.termsText && (
          <Text
            tag='p'
            className={styles.termsText}
          >
            {data.termsText}
          </Text>
        )}
      </div>
    </section>
  );
};

export default PlansSection;
